import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';

/**
 * Turns a failure into a sentence in the user's language.
 *
 * The server's `code` is the contract; its `message` is developer-facing text
 * in whatever language the backend was written in and must never render
 * (review §4.12 — an English UI was surfacing Chinese backend prose). An
 * unmapped code therefore falls back to the translated generic sentence, not
 * to the server's wording.
 */
export function useApiError() {
  const { t, i18n } = useTranslation();

  return useCallback(
    (error: unknown, fallbackKey = 'errors.unknown_error'): string => {
      const code = error instanceof ApiError ? error.code : null;
      if (code && i18n.exists(`errors.${code}`)) return t(`errors.${code}`);
      return t(fallbackKey);
    },
    [t, i18n],
  );
}
